import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { COIN_VALUE_STYLE } from '@/containers/common/TextStyles'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolContainer } from '@/containers/symbols/SymbolContainer'
import Facade from '@/Facade'
import { IStoppingReelsData, Orientation, SymbolContainerData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { getHoldAndWinFinalWinAmount } from '@/states/holdandwin/HoldAndWinFinalWin'
import { getMaxWinAmount, isMaxWinCapReached } from '@/states/round/MaxWinCap'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { formatAmount, toCurrencySymbol } from '@modules/src/panel/PanelUtils'
import { formatCoinDisplayValue } from '@/utils/FormatCoinDisplayValue'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { ContainersNames } from '@/containers/ContainersNames'
import {
    HOLD_AND_WIN_ALLOWED_MULTIPLIERS,
    normalizeHoldAndWinCoinValue,
    pickHoldAndWinSpinCoin,
} from '@/utils/HoldAndWinCoinValue'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'

interface IBonusCell {
    wrapper: PIXI.Container
    frame: PIXI.Graphics
    spinMask: PIXI.Graphics
    symbol: SymbolContainer
}

interface ICollectEntry {
    cellIndex: number
    value: number
    type: string
}

export type HoldAndWinJackpotTier = 'mini' | 'minor' | 'major' | 'grand'

//## Jackpot Popup sequence (ascending).
const HOLD_AND_WIN_JACKPOT_TIER_PRIORITY: HoldAndWinJackpotTier[] = ['mini', 'minor', 'major', 'grand']

//## Live reference to the active board, so states can read the collected jackpot tiers.
let activeBonusRespinBoard: BonusRespinBoardContainer | null = null

/**
 * Unique jackpot tiers currently locked on the board, sorted mini -> minor -> major -> grand.
 * Read after the collect completes (before the board exits) to drive the Jackpot Popup.
 */
export const getCollectedHoldAndWinJackpotTiers = (): HoldAndWinJackpotTier[] => {
    return activeBonusRespinBoard ? activeBonusRespinBoard.getCollectedJackpotTiers() : []
}

/**
 * Jackpot popup tiers that still need to play after collect (not already shown inline
 * while collecting each jackpot coin / full-board mini).
 */
export const getPendingHoldAndWinJackpotPopupTiers = (): HoldAndWinJackpotTier[] => {
    return activeBonusRespinBoard ? activeBonusRespinBoard.getPendingJackpotPopupTiers() : []
}

/**
 * True only when every board cell is filled with a (non-jackpot) Golden Coin:
 * 15 locked coins and no Mini/Minor/Major/Grand jackpot symbols present.
 */
export const isHoldAndWinFullGoldenBoard = (): boolean => {
    return activeBonusRespinBoard ? activeBonusRespinBoard.isFullGoldenCoinBoard() : false
}

export class BonusRespinBoardContainer extends PIXI.Container {
    private static readonly COLUMNS = 5
    private static readonly ROWS = 3
    private static readonly CELLS_COUNT = BonusRespinBoardContainer.COLUMNS * BonusRespinBoardContainer.ROWS
    private static readonly DEBUG_SHOW_CELL_MASK_OUTLINES = false
    private static normalizeCoinAmount(value: number): number {
        if (!Number.isFinite(value)) {
            return 0
        }

        return Math.round((value + Number.EPSILON) * 100) / 100
    }

    private static readonly NORMAL_TIMINGS = {
        rollDurationMin: 0.33,
        rollDurationMax: 0.46,
        restartDelayMin: 0.025,
        restartDelayMax: 0.115,
        exitMinDuration: 0.16,
        stopStagger: 0.11,
        startDelayMax: 0.62,
        settleGap: 0.4,
        minVisibleSpinTime: 0.55,
    }

    private static readonly FAST_TIMINGS = {
        rollDurationMin: 0.24,
        rollDurationMax: 0.34,
        restartDelayMin: 0.015,
        restartDelayMax: 0.07,
        exitMinDuration: 0.12,
        stopStagger: 0.065,
        startDelayMax: 0.24,
        settleGap: 0.18,
        minVisibleSpinTime: 0.22,
    }

    private static readonly COLLECT_TIMINGS = {
        normal: {
            preCollectDelay: 0.2,
            /** Gap between prize-coin collects (non-jackpot). */
            stepDelay: 0.16,
            /** Short settle after jackpot values land before the popup opens. */
            jackpotReadDelay: 0.45,
            /**
             * Hold COLLECT amount on the ticker after a jackpot popup closes,
             * before the next jackpot fly / popup starts.
             */
            jackpotGapDelay: 2.0,
            flyDuration: 0.45,
            settleDelay: 0.18,
        },
        fast: {
            preCollectDelay: 0.1,
            stepDelay: 0.08,
            /** Still readable in turbo; skip clears these waits immediately. */
            jackpotReadDelay: 0.25,
            jackpotGapDelay: 0.9,
            flyDuration: 0.28,
            settleDelay: 0.12,
        },
    }

    //## Collect label travel: it leaves the coin at full size, shrinks all the way to the ticker box,
    //## and fades out over the last stretch. travelEndScale is a multiplier of the label's own scale,
    //## fadeStartAt is the fraction of the travel that plays before the fade begins.
    private static readonly COLLECT_FLY = {
        travelEndScale: 0.45,
        fadeStartAt: 0.75,
        fadeDuration: 0.2,
        fastFadeDuration: 0.2,
    }

    // Dimmed static look after a coin finishes collecting, until commendations / board exit.
    private static readonly COLLECTED_COIN_TINT = 0x9a7a58
    private static get CELL_SYMBOL_STOP_Y(): number {
        return ReelsContainerHelper.calculateReelHeight() / 2
    }

    private static readonly TICKER_CONTAINER_NAME = 'TickerMessageContainer'
    private static readonly TICKER_BG_NAME = 'TickerMessageBg'

    //## Tuning hook: nudges where the collect labels land, relative to the centre of the ticker box.
    //## Positive x moves them right, positive y moves them down, in ticker-space pixels.
    private static readonly COLLECT_FLY_TARGET_OFFSET = new Map<Orientation, { x: number; y: number }>([
        [Orientation.LANDSCAPE, { x: 100, y: 0 }],
        [Orientation.PORTRAIT, { x: 100, y: 0 }],
    ])

    private readonly _cells: IBonusCell[] = []
    private _rollingActive: boolean[] = []
    private _rollingTweens: Array<gsap.core.Tween | null> = []
    private _startDelayCalls: Array<gsap.core.Tween | null> = []
    private _cellRollDuration: number[] = []
    private _cellRestartDelay: number[] = []
    private _stickyLandingTweens: Array<gsap.core.Tween | null> = []
    private readonly _respinCounterContainer: PIXI.Container = new PIXI.Container()
    private readonly _respinCounterText: PIXI.Text = new PIXI.Text('RESPIN LEFT: 0', {
        fontFamily: 'Arial',
        fontSize: 34,
        fontWeight: '700',
        fill: 0xffd565,
        stroke: {
            color: 0x1f1308,
            width: 5,
        },
        align: 'center',
    })
    private _pendingRespinsCounterValue: number | null = null
    private _waitingStickyToApplyCounter: boolean = false
    private _pendingStopData: IStoppingReelsData | null = null
    private _lockedCoins: Map<number, { value: number; type: string }> = new Map()
    private _isSpinning: boolean = false
    private _isResolved: boolean = false
    private _hasEmittedBoardStopped: boolean = false
    private _isImmediateStopRequested: boolean = false
    private _isImmediateStopSettling: boolean = false
    private _spinStartedAt: number = 0
    private _hasEmittedBoardSpinStarted: boolean = false
    private _spinStartRespinsCounterValue: number = 0
    private _hasShownRespinEntryValue: boolean = false
    private _completeStopCall: gsap.core.Tween | null = null
    private _stopFinalizeCalls: gsap.core.Tween[] = []
    private _stopCoinsByCell: Map<number, { value: number; type: string }> = new Map()
    private _stopRollingOrder: number[] = []
    private _stopPreviouslyLocked: Set<number> = new Set()
    private static readonly IMMEDIATE_EXIT_DURATION = 0.06
    private _collectPlaceholderCall: gsap.core.Animation | null = null
    private _isCollectingPlaceholder: boolean = false
    private _isPendingExitAfterCollect: boolean = false
    private _pendingCollectArrivals: number = 0
    private _collectFlyAnimations: gsap.core.Animation[] = []
    private _collectFlyTexts: PIXI.DisplayObject[] = []
    private _collectSequenceEntries: ICollectEntry[] = []
    private _collectedEntryIndexes: Set<number> = new Set()
    private _collectedTotalDuringCollect: number = 0
    private _isSkippingCollectSequence: boolean = false
    //## Capped round: the collect stops as soon as this amount is reached, leaving the surplus coins behind.
    private _collectCapLimit: number | null = null
    //## Capped round: the coin values were never flown, so there is no sequence driving the phase to its end.
    private _isCappedCollectWithoutPresentation: boolean = false
    //## Survives the end of the collect phase so the leftover jackpot popups stay suppressed too.
    private _hasSkippedCollectForCap: boolean = false
    private _stickyLandShakeTimeline: gsap.core.Timeline | null = null
    private _collectReadoutUsesTicker: boolean = false
    private _cachedTickerContainer: PIXI.Container | null = null
    private _hasEmittedFullBoardThisRound: boolean = false
    // Jackpot tiers whose box win animation has already been announced this H&W session,
    // so an already-present or already-landed tier is not re-announced on every respin.
    private _winAnnouncedJackpotTiers: Set<string> = new Set()
    // Jackpot popup plays during collect (count = one per jackpot coin / full-board mini).
    private _jackpotPopupsShownDuringCollect: Set<HoldAndWinJackpotTier> = new Set()
    private _hasShownFullBoardMiniPopupDuringCollect: boolean = false
    private _jackpotPopupPlayCount: number = 0
    // Bumped to cancel an in-flight sequential collect when skipping.
    private _collectSequenceToken: number = 0

    public constructor() {
        super()

        activeBonusRespinBoard = this

        this.name = 'BONUS_RESPIN_BOARD_CONTAINER'
        this.visible = false

        this.createBoard()

        GameSignals.startSpin.connect(this.onStartSpin)
        GameSignals.stoppingReels.connect(this.onStoppingReels)
        GameSignals.immediatelyStop.connect(this.onImmediateStop)
        GameSignals.spinButtonClicked.connect(this.onCollectSkipInput)
        GameSignals.stopButtonClicked.connect(this.onCollectSkipInput)
        GameSignals.finalState.connect(this.onFinalState)
        GameSignals.roundStop.connect(this.onRoundStop)
        ;(GameSignals as any).holdAndWinStartSpin?.connect(this.onHoldAndWinStartSpin)
        ;(GameSignals as any).holdAndWinStopSpin?.connect(this.onHoldAndWinStopSpin)
        ;(GameSignals as any).holdAndWinStop?.connect(this.onHoldAndWinStop)
    }

    private createBoard(): void {
        this._cachedTickerContainer = null
        this.removeChildren()
        this._cells.length = 0
        this._rollingActive = new Array(BonusRespinBoardContainer.CELLS_COUNT).fill(false)
        this._rollingTweens = new Array(BonusRespinBoardContainer.CELLS_COUNT).fill(null)
        this._startDelayCalls = new Array(BonusRespinBoardContainer.CELLS_COUNT).fill(null)
        this._cellRollDuration = new Array(BonusRespinBoardContainer.CELLS_COUNT).fill(0)
        this._cellRestartDelay = new Array(BonusRespinBoardContainer.CELLS_COUNT).fill(0)
        this._stickyLandingTweens = new Array(BonusRespinBoardContainer.CELLS_COUNT).fill(null)

        const cellWidth = ReelsContainerHelper.calculateReelWidth()
        const cellHeight = ReelsContainerHelper.calculateReelHeight()
        const boardWidth = cellWidth * BonusRespinBoardContainer.COLUMNS

        this._respinCounterContainer.removeChildren()
        this._respinCounterContainer.name = 'HNW_RESPIN_COUNTER_CONTAINER'

        this._respinCounterContainer.x = boardWidth / 2
        this._respinCounterContainer.y = -34
        this._respinCounterContainer.visible = false

        this._respinCounterText.anchor.set(0.5)
        this._respinCounterText.resolution = 2
        this._respinCounterText.name = 'HNW_RESPIN_COUNTER_COLLECT_TEXT'
        this._respinCounterText.visible = false
        this._respinCounterContainer.addChild(this._respinCounterText)
        this.addChild(this._respinCounterContainer)

        for (let row = 0; row < BonusRespinBoardContainer.ROWS; row++) {
            for (let col = 0; col < BonusRespinBoardContainer.COLUMNS; col++) {
                const wrapper = new PIXI.Container()
                wrapper.name = `HNW_CELL_${row}_${col}`
                wrapper.x = col * cellWidth + 28
                wrapper.y = row * cellHeight

                const frame = new PIXI.Graphics()
                const spinMask = new PIXI.Graphics()
                const maskOutline = new PIXI.Graphics()
                const symbol = new SymbolContainer()
                symbol.x = cellWidth / 2
                symbol.y = BonusRespinBoardContainer.CELL_SYMBOL_STOP_Y
                symbol.visible = false
                frame.visible = false

                spinMask.beginFill(0xffffff, 1)
                spinMask.drawRoundedRect(0, 0, cellWidth - 1, cellHeight - 1, 16)
                spinMask.endFill()
                spinMask.visible = true
                spinMask.alpha = 0

                maskOutline.name = `HNW_CELL_MASK_OUTLINE_${row}_${col}`
                maskOutline.lineStyle(6, 0xff0000, 1)
                maskOutline.beginFill(0xff0000, 0.15)
                maskOutline.drawRoundedRect(4, 4, cellWidth - 9, cellHeight - 9, 12)
                maskOutline.endFill()
                maskOutline.visible = BonusRespinBoardContainer.DEBUG_SHOW_CELL_MASK_OUTLINES
                maskOutline.eventMode = 'none'

                wrapper.addChild(frame, symbol, spinMask, maskOutline)
                symbol.mask = spinMask
                this.drawCellFrame(frame, false)

                this._cells.push({ wrapper, frame, spinMask, symbol })
                this.addChild(wrapper)
            }
        }
    }

    private onHoldAndWinStartSpin = () => {
        this._hasEmittedFullBoardThisRound = false
        this.startBoardSpin()
    }

    private onStartSpin = () => {
        if (!Facade.model?.spinState?.isHoldAndWinActive) {
            if (this.visible || this._isSpinning || this._isCollectingPlaceholder || this._isPendingExitAfterCollect) {
                this.exitRespinToBaseGame()
            }
            return
        }

        this.startBoardSpin()
    }

    private startBoardSpin(): void {
        if (this._isSpinning && !this._isResolved) {
            return
        }

        this.resetAllCellMasks(true)

        this.visible = true
        this._isSpinning = true
        this._isResolved = false
        this._hasEmittedBoardStopped = false
        this._hasEmittedBoardSpinStarted = false
        this._spinStartRespinsCounterValue = Math.max(0, Number((Facade.model?.spinData as any)?.respins_left ?? 0) - 1)
        this._isImmediateStopRequested = false
        this._isImmediateStopSettling = false
        this._isCollectingPlaceholder = false
        this._isPendingExitAfterCollect = false
        this._pendingStopData = null
        ;(Facade as any).holdAndWinCollectInProgress = false
        ;(Facade as any).holdAndWinPendingBaseTransition = false
        this.clearCollectFlyEffects()
        if (this._completeStopCall) {
            this._completeStopCall.kill()
            this._completeStopCall = null
        }
        this.clearStopFinalizeCalls()
        if (this._collectPlaceholderCall) {
            this._collectPlaceholderCall.kill()
            this._collectPlaceholderCall = null
        }
        this._spinStartedAt = Date.now()

        this.hydrateLockedCoinsFromModel()
        this.updateRespinsCounterAtRespinStartFromModel()
        this.renderLockedCoins()
        // Jackpot symbols already on the board when the feature is entered (the Continue moment,
        // and every restored/ongoing respin) should immediately loop their box win animation.
        this.announcePresentJackpotTiers()
        this.setupCellCadenceProfile()

        for (let index = 0; index < this._cells.length; index++) {
            if (this._lockedCoins.has(index)) {
                this.stopCellRolling(index)
                continue
            }

            this.scheduleCellRollingStart(index)
        }
    }

    private onHoldAndWinStopSpin = (data: IStoppingReelsData) => {
        this.onStoppingReels(data)
    }

    private onStoppingReels = (data: IStoppingReelsData) => {
        const modelSpinData: any = Facade.model?.spinData || {}
        const currentSpinType = String(modelSpinData?.spin_type || '').toLowerCase()
        const isHoldAndWinRespin =
            data.isHoldAndWinRespin ||
            currentSpinType === 'respin' ||
            Facade.model?.spinState?.isHoldAndWinActive === true

        if (!isHoldAndWinRespin) {
            if (this.visible || this._isSpinning || this._isCollectingPlaceholder || this._isPendingExitAfterCollect) {
                this.exitRespinToBaseGame()
            }
            return
        }

        this.visible = true
        this._pendingStopData = data

        if (typeof data.holdAndWinSpinsLeft === 'number' && Number.isFinite(data.holdAndWinSpinsLeft)) {
            this._pendingRespinsCounterValue = Number(data.holdAndWinSpinsLeft)
        } else {
            this._pendingRespinsCounterValue = Number((Facade.model?.spinData as any)?.respins_left ?? 0)
        }

        if (!this._isSpinning) {
            this._isSpinning = true
            this.setupCellCadenceProfile()
            for (let index = 0; index < this._cells.length; index++) {
                if (this._lockedCoins.has(index)) {
                    this.stopCellRolling(index)
                    continue
                }

                this.scheduleCellRollingStart(index)
            }
        }

        this.stopWithFinalResult(data, this._isImmediateStopRequested)
    }

    private onImmediateStop = () => {
        if (this._isImmediateStopSettling || this._hasEmittedBoardStopped) {
            return
        }

        this._isImmediateStopRequested = true

        if (this._isCollectingPlaceholder) {
            this.skipCollectPlaceholder()
            return
        }

        if (this._pendingStopData) {
            this.stopWithFinalResult(this._pendingStopData, true)
        }
    }

    /** Space / spin / stop during collect — panel is in Spin mode after reels stop. */
    private onCollectSkipInput = () => {
        if (!this._isCollectingPlaceholder) {
            return
        }

        this.skipCollectPlaceholder()
    }

    private onFinalState = () => {
        const spinType = String((Facade.model?.spinData as any)?.spin_type || '').toLowerCase()
        const hasHoldAndWinContext =
            this.visible ||
            this._isSpinning ||
            this._isCollectingPlaceholder ||
            this._isPendingExitAfterCollect ||
            this._lockedCoins.size > 0 ||
            spinType === 'respin' ||
            Facade.model?.spinState?.isHoldAndWinActive === true ||
            Facade.model?.isBonusGameNow === true

        if (!hasHoldAndWinContext) {
            return
        }

        const nextRoundType = String((Facade.model?.spinData as any)?.next_round_type || '').toLowerCase()
        const respinsLeft = Number((Facade.model?.spinData as any)?.respins_left ?? 0)
        const keepVisible = nextRoundType === 'respins' && Number.isFinite(respinsLeft) && respinsLeft > 0

        if (!keepVisible) {
            this.beginCollectPlaceholderOrExit()
            return
        }
    }

    private onRoundStop = () => {
        if (!this._isPendingExitAfterCollect) {
            return
        }

        this.exitRespinToBaseGame()
    }

    private onHoldAndWinStop = () => {
        this.stopAllRollingTimers()
        this.clearCollectFlyEffects()
        this._respinCounterContainer.visible = false
        this.visible = false
    }

    private beginCollectPlaceholderOrExit(): void {
        if (this._isCollectingPlaceholder || this._isPendingExitAfterCollect) {
            return
        }

        // Collection phase has begun: stop any looping jackpot box win animations.
        ;(GameSignals as any).holdAndWinCollectStarted?.emit()

        const hasLockedCoins = this._lockedCoins.size > 0
        if (!hasLockedCoins) {
            this.completeCollectPlaceholder()
            return
        }

        this._isCollectingPlaceholder = true
        ;(Facade as any).holdAndWinCollectInProgress = true
        ;(Facade as any).holdAndWinPendingBaseTransition = false
        this.stopAllRollingTimers()
        this.forceSnapAllCellsToFinalState()
        // this._respinCounterContainer.visible = true
        // this._respinCounterText.visible = true

        //## Prefer the ticker message as the collect readout (the collect labels fly to it).
        //## The board's own overlay is kept only as a fallback when the ticker is unavailable.
        this._collectReadoutUsesTicker = this.findTickerContainer() !== null
        this._respinCounterContainer.visible = !this._collectReadoutUsesTicker
        this._respinCounterText.visible = !this._collectReadoutUsesTicker
        this._respinCounterText.text = `COLLECT: ${this.formatCollectAmount(0)}`
        this.emitCollectProgress(0)

        const lockedEntries = Array.from(this._lockedCoins.entries()).filter(([, coinData]) => {
            return Boolean(coinData) && Number(coinData.value) > 0
        })
        if (lockedEntries.length === 0) {
            this.completeCollectPlaceholder()
            return
        }

        const collectTimings =
            Facade.isTurboSpin || Facade.isQuickSpin
                ? BonusRespinBoardContainer.COLLECT_TIMINGS.fast
                : BonusRespinBoardContainer.COLLECT_TIMINGS.normal
        const orderedLockedEntries = this.sortEntriesLeftToRight(lockedEntries).map(([cellIndex, coinData]) => ({
            cellIndex,
            value: Math.max(0, Number(coinData.value) || 0),
            type: String(coinData.type || 'prize'),
        }))
        this._collectSequenceEntries = orderedLockedEntries
        this._collectedEntryIndexes.clear()
        this._collectedTotalDuringCollect = 0
        this._isSkippingCollectSequence = false
        this._collectCapLimit = this.resolveCollectCapLimit()
        this._isCappedCollectWithoutPresentation = false
        this._hasSkippedCollectForCap = false
        this._pendingCollectArrivals = orderedLockedEntries.length
        this._jackpotPopupsShownDuringCollect.clear()
        this._hasShownFullBoardMiniPopupDuringCollect = false
        this._jackpotPopupPlayCount = 0

        if (this._collectPlaceholderCall) {
            this._collectPlaceholderCall.kill()
            this._collectPlaceholderCall = null
        }

        this.clearCollectFlyEffects()

        if (this.shouldSkipCollectPresentation(orderedLockedEntries)) {
            this.completeCappedCollectWithoutPresentation(collectTimings.jackpotReadDelay)
            return
        }

        // 1) Trigger win animation on all locked coins at the same time.
        orderedLockedEntries.forEach(({ cellIndex, value, type }) => {
            const cell = this._cells[cellIndex]
            if (!cell) {
                return
            }

            // Collect win animation scales up slightly; disable mask to avoid clipping.
            this.setCellMaskEnabled(cell, false)

            this.renderCoin(cell.symbol, value, type, false)
            cell.symbol.y = BonusRespinBoardContainer.CELL_SYMBOL_STOP_Y
            cell.symbol.tint = 0xffffff
            cell.symbol.alpha = 1
            cell.symbol.playAnimation('win', true, null, true)
        })

        // 2) Jackpot coins by tier (batched), then regular prize coins one-by-one L→R.
        this._collectSequenceToken += 1
        const sequenceToken = this._collectSequenceToken
        void this.runSequentialCollect(sequenceToken, orderedLockedEntries, collectTimings)
    }

    /**
     * The coins on the board can be worth more than the round is allowed to pay: the collect flies
     * coins only up to the limit and the surplus is left on the board, so the readout matches the
     * end popup. The server total is authoritative once the round is flagged as capped; the max win
     * for the stake in play is a ceiling on every round. Null means collect everything.
     *
     * Both limits arrive in minor units and are converted here: the coin values on the board are
     * stake-multiplied major units, so comparing the two raw would put the limit 100x too high.
     */
    private resolveCollectCapLimit(): number | null {
        const model = Facade.model
        if (!model) {
            return null
        }

        const limits = [
            isMaxWinCapReached(model) ? toMajorUnits(getHoldAndWinFinalWinAmount(model)) : 0,
            toMajorUnits(getMaxWinAmount(model)),
        ].filter((limit) => limit > 0)

        return limits.length ? Math.min(...limits) : null
    }

    private isCollectCapReached(): boolean {
        return this._collectCapLimit !== null && this._collectedTotalDuringCollect >= this._collectCapLimit
    }

    /** What the collect would add up to if every coin flew, including the full-board Mini award. */
    private resolveCollectableBoardTotal(orderedLockedEntries: ICollectEntry[]): number {
        const coinsTotal = orderedLockedEntries.reduce((total, { value }) => total + value, 0)
        const fullBoardMini = this.isFullBoard() ? this.resolveFullBoardMiniAward() : 0

        return BonusRespinBoardContainer.normalizeCoinAmount(coinsTotal + fullBoardMini)
    }

    /**
     * The board is worth more than the round may pay. Flying the values one by one would either
     * read past the limit or stall half way with coins left uncollected, so the whole collect
     * presentation is dropped and the round goes on to the commendation, the total win popup and
     * the max exposure message, which are what the player is allowed to be shown.
     */
    private shouldSkipCollectPresentation(orderedLockedEntries: ICollectEntry[]): boolean {
        return (
            this._collectCapLimit !== null &&
            this.resolveCollectableBoardTotal(orderedLockedEntries) > this._collectCapLimit
        )
    }

    /** Amount the end popup will present: the feature total for the round, never above the limit. */
    private resolveCappedCollectReadout(): number {
        const capLimit = this._collectCapLimit ?? 0
        const model = Facade.model
        const featureTotal = model ? toMajorUnits(getHoldAndWinFinalWinAmount(model)) : 0
        const readout = Number.isFinite(featureTotal) && featureTotal > 0 ? Math.min(featureTotal, capLimit) : capLimit

        return BonusRespinBoardContainer.normalizeCoinAmount(readout)
    }

    /** Readout jumps straight to the capped total, held long enough to be read, then the phase ends. */
    private completeCappedCollectWithoutPresentation(readDelay: number): void {
        this._isCappedCollectWithoutPresentation = true
        this._hasSkippedCollectForCap = true
        this._pendingCollectArrivals = 0
        this._collectedTotalDuringCollect = this.resolveCappedCollectReadout()
        this._respinCounterText.text = `COLLECT: ${this.formatCollectAmount(this._collectedTotalDuringCollect)}`
        this.emitCollectProgress(this._collectedTotalDuringCollect)

        // No sequence runs on this path, so any token still in flight must not resume.
        this._collectSequenceToken += 1
        this._collectPlaceholderCall = gsap.delayedCall(Math.max(0, readDelay), () => {
            this._collectPlaceholderCall = null
            this.completeCollectPlaceholder()
        })
    }

    /** The coin that crosses the limit flies only the part that still fits, so the readout adds up. */
    private resolveCollectFlyValue(value: number): number {
        if (this._collectCapLimit === null) {
            return value
        }

        return Math.max(0, Math.min(value, this._collectCapLimit - this._collectedTotalDuringCollect))
    }

    private addToCollectedTotal(value: number): void {
        const total = this._collectedTotalDuringCollect + value

        this._collectedTotalDuringCollect =
            this._collectCapLimit !== null ? Math.min(total, this._collectCapLimit) : total
    }

    private async runSequentialCollect(
        sequenceToken: number,
        orderedLockedEntries: ICollectEntry[],
        collectTimings: {
            preCollectDelay: number
            stepDelay: number
            jackpotReadDelay: number
            jackpotGapDelay: number
            flyDuration: number
            settleDelay: number
        }
    ): Promise<void> {
        const isCancelled = () => sequenceToken !== this._collectSequenceToken

        await this.waitCollectSeconds(collectTimings.preCollectDelay)
        if (isCancelled()) {
            return
        }

        // A full board awards Mini independently from any jackpot coins on the grid.
        if (this.isFullBoard()) {
            this._hasShownFullBoardMiniPopupDuringCollect = true
            await this.playJackpotPopupInline('mini', { trackJackpotGroup: false })
            if (isCancelled()) {
                return
            }

            this.addToCollectedTotal(this.resolveFullBoardMiniAward())
            this._collectedTotalDuringCollect = BonusRespinBoardContainer.normalizeCoinAmount(
                this._collectedTotalDuringCollect
            )
            this._respinCounterText.text = `COLLECT: ${this.formatCollectAmount(this._collectedTotalDuringCollect)}`
            this.emitCollectProgress(this._collectedTotalDuringCollect)
            // Hold so the Mini award amount is readable on the ticker before cell collect continues.
            await this.waitCollectSeconds(collectTimings.jackpotReadDelay)
            if (isCancelled()) {
                return
            }
        }

        await this.collectJackpotGroupsThenPrizes(orderedLockedEntries, collectTimings, false)
        if (isCancelled()) {
            return
        }

        // Skip path may have interrupted mid-sequence — finish remaining coins + popups.
        // Not when the cap stopped it: the coins past the limit are meant to stay uncollected.
        if (this._isSkippingCollectSequence && !this.isCollectCapReached()) {
            await this.finishRemainingCollectAfterSkip(collectTimings.flyDuration)
            if (isCancelled()) {
                return
            }
        }

        await this.waitCollectSeconds(collectTimings.settleDelay)
        if (isCancelled()) {
            return
        }

        this.completeCollectPlaceholder()
    }

    /**
     * 1) For each jackpot tier (mini → minor → major → grand): collect every coin of that
     *    type together, then play that tier's popup once.
     * 2) Then collect remaining regular prize coins left-to-right, one at a time.
     */
    private async collectJackpotGroupsThenPrizes(
        orderedLockedEntries: ICollectEntry[],
        collectTimings: {
            stepDelay: number
            jackpotReadDelay?: number
            jackpotGapDelay?: number
            flyDuration: number
        },
        fast: boolean
    ): Promise<void> {
        const { jackpotGroups, prizeItems } = this.partitionCollectEntries(orderedLockedEntries)
        const flyDuration = fast ? Math.min(0.2, collectTimings.flyDuration * 0.35) : collectTimings.flyDuration
        const jackpotReadDelay = collectTimings.jackpotReadDelay ?? 0
        const jackpotGapDelay = collectTimings.jackpotGapDelay ?? collectTimings.stepDelay

        for (let groupIndex = 0; groupIndex < jackpotGroups.length; groupIndex++) {
            if (this.isCollectCapReached() || (!fast && this._isSkippingCollectSequence)) {
                return
            }

            const { tier, items } = jackpotGroups[groupIndex]
            const pendingItems = items.filter(({ index }) => !this._collectedEntryIndexes.has(index))
            if (pendingItems.length === 0) {
                continue
            }

            // Same-tier jackpots fly together — one fly/hit SFX for the whole group.
            await Promise.all(
                pendingItems.map(({ entry, index }, itemIndex) =>
                    this.collectSingleEntry(entry, index, flyDuration, false, itemIndex === 0)
                )
            )
            if (this.isCollectCapReached() || (!fast && this._isSkippingCollectSequence)) {
                return
            }

            // COLLECT total just updated on the ticker — hold so the amount is readable
            // before the jackpot popup replaces the message.
            if (!fast && jackpotReadDelay > 0) {
                await this.waitCollectSeconds(jackpotReadDelay)
                if (this._isSkippingCollectSequence) {
                    return
                }
            }

            if (!this._jackpotPopupsShownDuringCollect.has(tier)) {
                const groupTotal = items.reduce((total, { entry }) => total + entry.value, 0)
                await this.playJackpotPopupInline(tier, {
                    amount: this.formatJackpotPopupAmount(groupTotal),
                })
                if (!fast && this._isSkippingCollectSequence) {
                    return
                }
            }

            // Popup replaces the ticker with the tier name — put COLLECT total back and hold
            // so the player can read the amount before the next jackpot popup starts.
            const collectedTotal = BonusRespinBoardContainer.normalizeCoinAmount(this._collectedTotalDuringCollect)
            this._respinCounterText.text = `COLLECT: ${this.formatCollectAmount(collectedTotal)}`
            this.emitCollectProgress(collectedTotal)

            const hasMoreJackpotGroups = jackpotGroups
                .slice(groupIndex + 1)
                .some(({ items: nextItems }) => nextItems.some(({ index }) => !this._collectedEntryIndexes.has(index)))
            const hasPendingPrizes = prizeItems.some(({ index }) => !this._collectedEntryIndexes.has(index))
            if (!fast && jackpotGapDelay > 0 && (hasMoreJackpotGroups || hasPendingPrizes)) {
                await this.waitCollectSeconds(jackpotGapDelay)
                if (this._isSkippingCollectSequence) {
                    return
                }
                // Re-assert COLLECT in case another ticker event overwrote it during the hold.
                this.emitCollectProgress(
                    BonusRespinBoardContainer.normalizeCoinAmount(this._collectedTotalDuringCollect)
                )
            }
        }

        const pendingPrizes = prizeItems.filter(({ index }) => !this._collectedEntryIndexes.has(index))
        if (pendingPrizes.length === 0 || this.isCollectCapReached()) {
            return
        }

        const hasPendingJackpots = jackpotGroups.some(({ items }) =>
            items.some(({ index }) => !this._collectedEntryIndexes.has(index))
        )

        // Skip / fast path once jackpots are done (or none): fly every remaining prize together.
        // A capped round always goes coin by coin, so the collect can stop exactly on the limit.
        if (this._collectCapLimit === null && (fast || (this._isSkippingCollectSequence && !hasPendingJackpots))) {
            await Promise.all(
                pendingPrizes.map(({ entry, index }, itemIndex) =>
                    this.collectSingleEntry(entry, index, flyDuration, true, itemIndex === 0)
                )
            )
            return
        }

        for (let i = 0; i < pendingPrizes.length; i++) {
            if (this.isCollectCapReached() || (!fast && this._isSkippingCollectSequence)) {
                return
            }

            const { entry, index } = pendingPrizes[i]
            await this.collectSingleEntry(entry, index, flyDuration, fast)
            if (this.isCollectCapReached() || (!fast && this._isSkippingCollectSequence)) {
                return
            }

            if (i < pendingPrizes.length - 1) {
                await this.waitCollectSeconds(collectTimings.stepDelay)
            }
        }
    }

    private partitionCollectEntries(orderedLockedEntries: ICollectEntry[]): {
        jackpotGroups: Array<{ tier: HoldAndWinJackpotTier; items: Array<{ entry: ICollectEntry; index: number }> }>
        prizeItems: Array<{ entry: ICollectEntry; index: number }>
    } {
        const itemsByTier = new Map<HoldAndWinJackpotTier, Array<{ entry: ICollectEntry; index: number }>>()
        const prizeItems: Array<{ entry: ICollectEntry; index: number }> = []

        orderedLockedEntries.forEach((entry, index) => {
            const tier = this.resolveJackpotTier(entry.type)
            if (!tier) {
                prizeItems.push({ entry, index })
                return
            }

            const group = itemsByTier.get(tier) || []
            group.push({ entry, index })
            itemsByTier.set(tier, group)
        })

        const jackpotGroups = HOLD_AND_WIN_JACKPOT_TIER_PRIORITY.filter((tier) => itemsByTier.has(tier)).map(
            (tier) => ({
                tier,
                items: itemsByTier.get(tier)!,
            })
        )

        return { jackpotGroups, prizeItems }
    }

    private waitCollectSeconds(seconds: number): Promise<void> {
        if (seconds <= 0) {
            return Promise.resolve()
        }

        return new Promise((resolve) => {
            let settled = false
            const finish = () => {
                if (settled) {
                    return
                }
                settled = true
                resolve()
            }

            const delayCall = gsap.delayedCall(seconds, finish)
            const originalKill = delayCall.kill.bind(delayCall)
            ;(delayCall as any).kill = (...args: any[]) => {
                originalKill(...args)
                finish()
            }
            this._collectFlyAnimations.push(delayCall)
        })
    }

    private playJackpotPopupInline(
        tier: HoldAndWinJackpotTier,
        options: { trackJackpotGroup?: boolean; amount?: string } = {}
    ): Promise<void> {
        if (options.trackJackpotGroup !== false) {
            this._jackpotPopupsShownDuringCollect.add(tier)
        }
        this._jackpotPopupPlayCount += 1

        return new Promise((resolve) => {
            let settled = false
            let safetyCall: gsap.core.Tween | null = null
            const finish = () => {
                if (settled) {
                    return
                }
                settled = true
                ;(GameSignals as any).jackpotPopupComplete?.disconnect(onComplete)
                if (safetyCall) {
                    safetyCall.kill()
                    safetyCall = null
                }
                resolve()
            }

            const onComplete = () => finish()
            ;(GameSignals as any).jackpotPopupComplete?.connect(onComplete)
            ;(GameSignals as any).jackpotPopupStart?.emit([options.amount ? { tier, amount: options.amount } : tier])

            // Safety: never block collect forever if the popup component fails to complete.
            // Keep this out of `_collectFlyAnimations` so skip does not abort a live popup.
            safetyCall = gsap.delayedCall(7, finish)
        })
    }

    private async finishRemainingCollectAfterSkip(flyDuration: number): Promise<void> {
        const { jackpotGroups } = this.partitionCollectEntries(this._collectSequenceEntries)
        const hasJackpots = jackpotGroups.some(({ items }) =>
            items.some(({ index }) => !this._collectedEntryIndexes.has(index))
        )

        // No jackpots left: dump every remaining prize coin in one simultaneous wave.
        await this.collectJackpotGroupsThenPrizes(
            this._collectSequenceEntries,
            { stepDelay: 0, flyDuration: hasJackpots ? flyDuration : Math.min(0.28, flyDuration) },
            true
        )
    }

    private completeCollectPlaceholder(): void {
        this._isCollectingPlaceholder = false
        this._isPendingExitAfterCollect = true
        this._pendingCollectArrivals = 0
        this._collectSequenceEntries = []
        this._collectedEntryIndexes.clear()
        this._collectedTotalDuringCollect = 0
        this._isSkippingCollectSequence = false
        this._collectCapLimit = null
        this._isCappedCollectWithoutPresentation = false

        // All collected coins stay dimmed & static through jackpot popups / commendations.
        this.applyCollectedVisualToLockedCoins()

        ;(Facade as any).holdAndWinCollectInProgress = false
        ;(Facade as any).holdAndWinPendingBaseTransition = true
        ;(GameSignals as any).holdAndWinFullBoardCleared?.emit()
        ;(GameSignals as any).holdAndWinCollectComplete?.emit()
    }

    private setCoinCollectedVisual(symbol: SymbolContainer): void {
        // Freeze idle spine (same size as in-feature coins). forceStatic() uses the
        // prize static texture which reads larger than the spine after collect.
        symbol.freezeAtIdle()
        symbol.tint = BonusRespinBoardContainer.COLLECTED_COIN_TINT
        symbol.alpha = 1
    }

    private applyCollectedVisualToLockedCoins(): void {
        this._lockedCoins.forEach((_, cellIndex) => {
            const cell = this._cells[cellIndex]
            if (!cell?.symbol) {
                return
            }

            this.setCoinCollectedVisual(cell.symbol)
            // Win collect disabled the mask for the scaled-up win anim — restore it.
            this.setCellMaskEnabled(cell, true)
        })
    }

    private exitRespinToBaseGame(): void {
        const shouldEmitHoldAndWinStop = Boolean(
            Facade.model?.spinState?.isHoldAndWinActive ||
            Facade.model?.isBonusGameNow ||
            (Facade as any).holdAndWinCollectInProgress ||
            (Facade as any).holdAndWinPendingBaseTransition
        )

        this.stopAllRollingTimers()
        this.clearCollectFlyEffects()
        this.resetAllCellMasks(true)
        this._lockedCoins.clear()
        this._isSpinning = false
        this._isResolved = false
        this._hasEmittedBoardStopped = false
        this._isImmediateStopRequested = false
        this._isImmediateStopSettling = false
        this._winAnnouncedJackpotTiers.clear()
        this._jackpotPopupsShownDuringCollect.clear()
        this._hasShownFullBoardMiniPopupDuringCollect = false
        this._jackpotPopupPlayCount = 0
        this._isCappedCollectWithoutPresentation = false
        this._hasSkippedCollectForCap = false
        ;(Facade as any).holdAndWinCollectInProgress = false
        ;(Facade as any).holdAndWinPendingBaseTransition = false
        if (this._completeStopCall) {
            this._completeStopCall.kill()
            this._completeStopCall = null
        }
        this.clearStopFinalizeCalls()
        if (this._collectPlaceholderCall) {
            this._collectPlaceholderCall.kill()
            this._collectPlaceholderCall = null
        }
        if (Facade.model) {
            Facade.model.isBonusGameNow = false
            Facade.model.spinState.isHoldAndWinActive = false
        }
        this._hasShownRespinEntryValue = false
        this._respinCounterContainer.visible = false
        this.visible = false
        ;(GameSignals as any).holdAndWinFullBoardCleared?.emit()

        if (shouldEmitHoldAndWinStop) {
            ;(GameSignals as any).holdAndWinStop?.emit()
        }
    }

    private clearCollectFlyEffects(): void {
        this._collectFlyAnimations.forEach((animation) => animation.kill())
        this._collectFlyAnimations = []

        this._collectFlyTexts.forEach((displayObject) => {
            if (displayObject.parent) {
                displayObject.parent.removeChild(displayObject)
            }
            displayObject.destroy()
        })
        this._collectFlyTexts = []
    }

    //## The board sits below the ticker in the display tree, so a label parented to the board is drawn
    //## behind the ticker box. The label goes next to the ticker instead, sorted one step above it.
    //## It still has to start on top of its coin, so the coin position is converted into the new parent's
    //## space, and the label is rescaled because both parents can be scaled differently.
    //## Returns the parent the label was added to, which is where it has to fly to the ticker.
    private attachCollectFlyLabelAboveTicker(flyLabel: ScalingTextField, cell: IBonusCell): PIXI.Container {
        const tickerContainer = this.findTickerContainer()
        const flyParent = (tickerContainer?.parent as PIXI.Container) || this

        const coinPosition = cell.wrapper.toGlobal(new PIXI.Point(cell.symbol.x, cell.symbol.y - 4))
        flyLabel.position.copyFrom(flyParent.toLocal(coinPosition))
        flyLabel.scale.set(this.worldTransform.a / (flyParent.worldTransform.a || 1))
        flyLabel.alpha = 1

        if (flyParent !== this) {
            flyParent.sortableChildren = true
            flyLabel.zIndex = (Number(tickerContainer?.zIndex) || 0) + 1
        }

        flyParent.addChild(flyLabel)

        return flyParent
    }

    private collectSingleEntry(
        entry: ICollectEntry,
        entryOrderIndex: number,
        flyDuration: number,
        simultaneous: boolean = false,
        playFlySfx: boolean = true
    ): Promise<void> {
        if (this._collectedEntryIndexes.has(entryOrderIndex)) {
            return Promise.resolve()
        }

        const cell = this._cells[entry.cellIndex]
        if (!cell) {
            return Promise.resolve()
        }

        return new Promise((resolve) => {
            let settled = false
            const completeCoinCollect = () => {
                if (settled) {
                    return
                }
                settled = true

                if (!this._collectedEntryIndexes.has(entryOrderIndex)) {
                    this._collectedEntryIndexes.add(entryOrderIndex)
                    this.addToCollectedTotal(entry.value)

                    const collectedTotal = BonusRespinBoardContainer.normalizeCoinAmount(
                        this._collectedTotalDuringCollect
                    )
                    this._respinCounterText.visible = !this._collectReadoutUsesTicker
                    this._respinCounterText.text = `COLLECT: ${this.formatCollectAmount(collectedTotal)}`
                    this.emitCollectProgress(collectedTotal)

                    this.setCoinCollectedVisual(cell.symbol)
                    this.setCellMaskEnabled(cell, true)
                    this._pendingCollectArrivals = Math.max(0, this._pendingCollectArrivals - 1)
                }

                resolve()
            }

            // Jackpot and prize coins both fly their value to the ticker bob.
            const flyLabel = this.createCollectFlyLabel(this.resolveCollectFlyValue(entry.value))
            const flyParent = this.attachCollectFlyLabelAboveTicker(flyLabel, cell)
            this._collectFlyTexts.push(flyLabel)

            const flyTarget = this.getCollectFlyTarget(flyParent)

            const collectFly = BonusRespinBoardContainer.COLLECT_FLY
            const travelDuration = simultaneous ? Math.min(0.28, flyDuration * 0.55) : flyDuration
            const travelEndScale = flyLabel.scale.x * collectFly.travelEndScale
            const fadeDuration = simultaneous ? collectFly.fastFadeDuration : collectFly.fadeDuration
            const fadeStartTime = travelDuration * collectFly.fadeStartAt

            if (playFlySfx) {
                ;(GameSignals as any).holdAndWinCollectFlyStart?.emit()
            }

            const flyTimeline = gsap.timeline({
                onComplete: () => {
                    this.removeCollectFlyLabel(flyLabel)
                    completeCoinCollect()
                },
            })

            flyTimeline
                .to(
                    flyLabel,
                    {
                        x: flyTarget.x,
                        y: flyTarget.y,
                        duration: travelDuration,
                        ease: simultaneous ? 'power1.out' : 'power2.inOut',
                    },
                    0
                )
                .to(
                    flyLabel.scale,
                    {
                        x: travelEndScale,
                        y: travelEndScale,
                        duration: travelDuration,
                        ease: 'power1.in',
                    },
                    0
                )
                .to(
                    flyLabel,
                    {
                        alpha: 0,
                        duration: fadeDuration,
                        ease: 'power1.out',
                    },
                    fadeStartTime
                )

            if (playFlySfx) {
                // Hit the ticker at the end of travel (before the fade finishes).
                flyTimeline.call(
                    () => {
                        ;(GameSignals as any).holdAndWinCollectFlyHit?.emit()
                    },
                    null,
                    travelDuration
                )
            }

            const originalTimelineKill = flyTimeline.kill.bind(flyTimeline)
            ;(flyTimeline as any).kill = (...args: any[]) => {
                originalTimelineKill(...args)
                this.removeCollectFlyLabel(flyLabel)
                completeCoinCollect()
            }

            this._collectFlyAnimations.push(flyTimeline)
        })
    }

    private removeCollectFlyLabel(flyLabel: ScalingTextField): void {
        if (flyLabel.parent) {
            flyLabel.parent.removeChild(flyLabel)
        }

        flyLabel.destroy()
        this._collectFlyTexts = this._collectFlyTexts.filter((text) => text !== flyLabel)
    }

    private skipCollectPlaceholder(): void {
        if (!this._isCollectingPlaceholder || this._isSkippingCollectSequence) {
            return
        }

        this._isSkippingCollectSequence = true

        if (this._collectPlaceholderCall) {
            this._collectPlaceholderCall.kill()
            this._collectPlaceholderCall = null
        }

        // Capped round: nothing to fast-forward and no sequence to end the phase — close it here.
        if (this._isCappedCollectWithoutPresentation) {
            this.completeCollectPlaceholder()
            return
        }

        // Kill in-flight waits/flies (kill wrappers resolve promises) so
        // runSequentialCollect can finish remaining coins. Live jackpot popups keep running.
        this.clearCollectFlyEffects()
    }

    //## Function for finding exact container..!
    private findContainerByName(container: PIXI.Container | null, name: string): PIXI.Container | null {
        if (!container) {
            return null
        }

        if ((container as any).name === name || (container as any).label === name) {
            return container
        }

        for (const child of container.children) {
            const match = this.findContainerByName(child as PIXI.Container, name)
            if (match) {
                return match
            }
        }

        return null
    }

    //## Collect labels fly to the ticker box; fall back to the board's own collect overlay.
    private getCollectFlyTarget(flyParent: PIXI.Container): PIXI.Point {
        const tickerContainer = this.findTickerContainer()
        if (!tickerContainer) {
            const fallback = this.toGlobal(
                new PIXI.Point(this._respinCounterContainer.x, this._respinCounterContainer.y)
            )

            return this.applyCollectFlyTargetOffset(flyParent.toLocal(fallback))
        }

        //## The bg sprite is offset inside the ticker container in landscape, so prefer it as the visual centre.
        const tickerBg = this.findContainerByName(tickerContainer, BonusRespinBoardContainer.TICKER_BG_NAME)
        const target = tickerBg || tickerContainer

        return this.applyCollectFlyTargetOffset(flyParent.toLocal(target.getGlobalPosition(new PIXI.Point())))
    }

    //## Shifts the landing point by the configured offset for the current orientation.
    private applyCollectFlyTargetOffset(target: PIXI.Point): PIXI.Point {
        const offset = BonusRespinBoardContainer.COLLECT_FLY_TARGET_OFFSET.get(Facade.orientation as Orientation)

        if (offset) {
            target.x += offset.x
            target.y += offset.y
        }

        return target
    }

    private findTickerContainer(): PIXI.Container | null {
        if (
            this._cachedTickerContainer &&
            !this._cachedTickerContainer.destroyed &&
            this._cachedTickerContainer.parent
        ) {
            return this._cachedTickerContainer
        }

        this._cachedTickerContainer = null

        const roots: Array<PIXI.Container | null | undefined> = [
            Facade.container as PIXI.Container | null,
            Facade.app?.stage as PIXI.Container | undefined,
            (Facade as any).stage as PIXI.Container | undefined,
        ]

        for (const root of roots) {
            const tickerContainer = this.findContainerByName(
                root || null,
                BonusRespinBoardContainer.TICKER_CONTAINER_NAME
            )
            if (tickerContainer && !tickerContainer.destroyed) {
                this._cachedTickerContainer = tickerContainer
                return tickerContainer
            }
        }

        return null
    }

    private emitCollectProgress(total: number): void {
        ;(GameSignals as any).holdAndWinCollectProgress?.emit(
            BonusRespinBoardContainer.normalizeCoinAmount(Math.max(0, total))
        )
    }

    private formatCollectAmount(value: number): string {
        // Same currency formatting as jackpot popup / ticker COLLECT readout.
        return this.formatJackpotPopupAmount(value)
    }

    private formatJackpotPopupAmount(value: number): string {
        const normalized = BonusRespinBoardContainer.normalizeCoinAmount(value)
        const currency = String(Facade.model?.initData?.player?.currency ?? '')
        return `${toCurrencySymbol(currency)}${formatAmount(normalized)}`
    }

    private createCollectFlyLabel(value: number): ScalingTextField {
        const flyLabel = new ScalingTextField(1, 220, PIXI.BitmapText as any, COIN_VALUE_STYLE)
        flyLabel.setCentered(true)
        const normalized = BonusRespinBoardContainer.normalizeCoinAmount(value)
        flyLabel.text = Number.isFinite(normalized) && normalized > 0 ? formatCoinDisplayValue(normalized) : '0'
        return flyLabel
    }

    private updateRespinsCounterFromModel(): void {
        const respinsLeft = Number((Facade.model?.spinData as any)?.respins_left ?? 0)
        this.updateRespinsCounter(respinsLeft)
    }

    private updateRespinsCounterAtRespinStartFromModel(): void {
        const spinData: any = Facade.model?.spinData || {}
        const spinType = String(spinData?.spin_type || '').toLowerCase()
        const nextRoundType = String(spinData?.next_round_type || '').toLowerCase()
        const respinsLeft = Number(spinData?.respins_left ?? 0)
        const respinsTotal = Number(spinData?.respins_total ?? 0)
        const isFirstFeatureEntry =
            nextRoundType === 'respins' && spinType !== 'respin' && !this._hasShownRespinEntryValue

        if (isFirstFeatureEntry) {
            this._hasShownRespinEntryValue = true
            this.updateRespinsCounter(Math.max(0, Math.max(respinsTotal, respinsLeft)))
            gsap.delayedCall(0.08, () => {
                if (!this.visible) {
                    return
                }

                this.updateRespinsCounter(Math.max(0, respinsLeft - 1))
            })
            return
        }

        // The user-facing counter represents remaining respins AFTER the one that is starting now.
        this.updateRespinsCounter(Math.max(0, respinsLeft - 1))
    }

    private updateRespinsCounter(respinsLeftRaw: number): void {
        // Respin indicator UI is managed by a standalone component.
        // Keep collect overlay hidden unless collect sequence is active.
        this._respinCounterContainer.visible = this._isCollectingPlaceholder
    }

    private stopWithFinalResult(data: IStoppingReelsData, immediate: boolean): void {
        // Forced stop while a natural stop is already settling: finish every cell now.
        if (this._isResolved) {
            if (immediate && !this._hasEmittedBoardStopped && !this._isImmediateStopSettling) {
                this.accelerateToImmediateStop()
            }
            return
        }

        if (this._completeStopCall) {
            this._completeStopCall.kill()
            this._completeStopCall = null
        }
        this.clearStopFinalizeCalls()

        this._isResolved = true
        this._isImmediateStopSettling = immediate

        const coinsByCell = new Map<number, { value: number; type: string }>()
        const coinIndex = Array.isArray(data.holdAndWinCoinIndex) ? data.holdAndWinCoinIndex : []
        const coinValues = Array.isArray(data.holdAndWinCoinValues) ? data.holdAndWinCoinValues : []
        const coinTypes = Array.isArray(data.holdAndWinCoinTypes) ? data.holdAndWinCoinTypes : []
        const spinCoinValue = this.resolveSpinCoinValueForNormalize()

        coinIndex.forEach((rawFlatIndex, index) => {
            const flatIndex = Number(rawFlatIndex)
            if (!Number.isFinite(flatIndex) || flatIndex < 0 || flatIndex >= this._cells.length) {
                return
            }

            const normalizedCredit = Number(
                normalizeHoldAndWinCoinValue(coinValues[index], coinTypes[index], spinCoinValue) ?? 0
            )

            coinsByCell.set(flatIndex, {
                value: this.toStakeCoinValue(normalizedCredit),
                type: String(coinTypes[index] ?? 'prize'),
            })
        })

        const previouslyLocked = new Set(this._lockedCoins.keys())
        this._lockedCoins = coinsByCell
        this._stopCoinsByCell = coinsByCell
        this._stopPreviouslyLocked = previouslyLocked

        const isFullBoard = this._lockedCoins.size === BonusRespinBoardContainer.CELLS_COUNT
        if (isFullBoard && !this._hasEmittedFullBoardThisRound) {
            this._hasEmittedFullBoardThisRound = true

            const collectedTotalPreview = Array.from(this._lockedCoins.values()).reduce((total, coinData) => {
                return total + Math.max(0, Number(coinData?.value) || 0)
            }, 0)

            ;(GameSignals as any).holdAndWinFullBoardReached?.emit({
                lockedCount: this._lockedCoins.size,
                totalCells: BonusRespinBoardContainer.CELLS_COUNT,
                collectedTotalPreview,
            })
        }

        const newlyStickyCells = Array.from(coinsByCell.keys()).filter((index) => !previouslyLocked.has(index))
        this._waitingStickyToApplyCounter = newlyStickyCells.length > 0

        if (!this._waitingStickyToApplyCounter) {
            this.applyPendingRespinsCounterUpdate()
        }

        // Natural stop briefly spins new sticky cells first. Forced stop skips only that
        // rolling phase; the short sticky landing still has to remain visible.
        if (!immediate) {
            newlyStickyCells.forEach((index) => {
                if (!this._rollingActive[index]) {
                    this.scheduleCellRollingStart(index, true)
                }
            })
        }

        const rollingCandidates = Array.from({ length: this._cells.length }, (_, index) => index).filter(
            (index) => !previouslyLocked.has(index)
        )
        const stickyFirstOrder = this.buildStopOrder(rollingCandidates, newlyStickyCells)
        this._stopRollingOrder = stickyFirstOrder

        if (immediate) {
            stickyFirstOrder.forEach((cellIndex) => {
                const coinData = coinsByCell.get(cellIndex)
                const shouldPlayLanding = Boolean(coinData) && !previouslyLocked.has(cellIndex)
                this.finalizeCellStop(cellIndex, coinData, shouldPlayLanding, true)
            })
        } else {
            stickyFirstOrder.forEach((cellIndex, orderIndex) => {
                const timings = this.getTimingsProfile()
                const delay = orderIndex * timings.stopStagger + Math.random() * 0.07
                const call = gsap.delayedCall(delay, () => {
                    const coinData = coinsByCell.get(cellIndex)
                    const shouldPlayLanding = Boolean(coinData) && !previouslyLocked.has(cellIndex)
                    this.finalizeCellStop(cellIndex, coinData, shouldPlayLanding, false)
                })
                this._stopFinalizeCalls.push(call)
            })
        }

        const timings = this.getTimingsProfile()
        const elapsed = (Date.now() - this._spinStartedAt) / 1000
        const residualMinSpinTime = Math.max(0, timings.minVisibleSpinTime - elapsed)
        const stickyLandingDuration =
            newlyStickyCells.length > 0 ? (Facade.isTurboSpin || Facade.isQuickSpin ? 0.11 : 0.17) + 0.03 : 0
        const naturalSettleBudget = timings.exitMinDuration + stickyLandingDuration
        const completeDelay = immediate
            ? BonusRespinBoardContainer.IMMEDIATE_EXIT_DURATION + stickyLandingDuration
            : stickyFirstOrder.length * timings.stopStagger +
              timings.settleGap +
              residualMinSpinTime +
              naturalSettleBudget

        this._completeStopCall = gsap.delayedCall(completeDelay, () => {
            this.emitBoardStopped()
        })
    }

    /** Interrupt a natural settle: stop every remaining cell with the short exit, then complete. */
    private accelerateToImmediateStop(): void {
        if (this._isImmediateStopSettling) {
            return
        }

        this._isImmediateStopSettling = true
        this.clearStopFinalizeCalls()

        if (this._completeStopCall) {
            this._completeStopCall.kill()
            this._completeStopCall = null
        }

        const order =
            this._stopRollingOrder.length > 0
                ? this._stopRollingOrder
                : Array.from({ length: this._cells.length }, (_, index) => index).filter(
                      (index) => !this._stopPreviouslyLocked.has(index)
                  )

        order.forEach((cellIndex) => {
            const coinData = this._stopCoinsByCell.get(cellIndex) ?? this._lockedCoins.get(cellIndex)
            const shouldPlayLanding = Boolean(coinData) && !this._stopPreviouslyLocked.has(cellIndex)
            this.finalizeCellStop(cellIndex, coinData, shouldPlayLanding, true)
        })

        const hasNewStickyCells = order.some((cellIndex) => {
            const coinData = this._stopCoinsByCell.get(cellIndex) ?? this._lockedCoins.get(cellIndex)
            return Boolean(coinData) && !this._stopPreviouslyLocked.has(cellIndex)
        })
        const stickyLandingDuration = hasNewStickyCells
            ? (Facade.isTurboSpin || Facade.isQuickSpin ? 0.11 : 0.17) + 0.03
            : 0

        this._completeStopCall = gsap.delayedCall(
            BonusRespinBoardContainer.IMMEDIATE_EXIT_DURATION + stickyLandingDuration,
            () => {
                this.emitBoardStopped()
            }
        )
    }

    private clearStopFinalizeCalls(): void {
        this._stopFinalizeCalls.forEach((call) => call.kill())
        this._stopFinalizeCalls = []
    }

    private emitBoardStopped(): void {
        this.clearStopFinalizeCalls()
        // Safety snap so collect never starts with a mid-tween coin (last-respin glitch).
        this.forceSnapAllCellsToFinalState()
        this._completeStopCall = null
        this._isSpinning = false
        this._pendingStopData = null
        this._isImmediateStopRequested = false
        this._isImmediateStopSettling = false
        this._waitingStickyToApplyCounter = false
        this.applyPendingRespinsCounterUpdate()

        if (this._hasEmittedBoardStopped) {
            return
        }

        this._hasEmittedBoardStopped = true
        GameSignals.allReelsStopped.emit()
    }

    private applyPendingRespinsCounterUpdate(): void {
        if (this._pendingRespinsCounterValue === null) {
            return
        }

        const respinsLeft = this._pendingRespinsCounterValue
        this.updateRespinsCounter(respinsLeft)
        this._pendingRespinsCounterValue = null
        ;(GameSignals as any).holdAndWinRespinsCounterUpdated?.emit(respinsLeft)
    }

    private toStakeCoinValue(credit: number): number {
        const safeCredit = Number.isFinite(credit) && credit > 0 ? credit : 0
        const safeStake = this.resolveSelectedStakeForCoinValue()
        return BonusRespinBoardContainer.normalizeCoinAmount(safeCredit * safeStake)
    }

    private resolveFullBoardMiniAward(): number {
        const miniCredit = Number(normalizeHoldAndWinCoinValue(undefined, 'mini') ?? 0)
        return this.toStakeCoinValue(miniCredit)
    }

    /**
     * Same stake as base-game ReelContainer: total bet in major units.
     * Do NOT use coin-value here — that incorrectly scales H&W labels (e.g. 4 → 20).
     */
    private resolveSelectedStakeForCoinValue(): number {
        const currentBetRaw = Number(Facade.model?.currentBet ?? 0)
        if (Number.isFinite(currentBetRaw) && currentBetRaw > 0) {
            return toMajorUnits(currentBetRaw)
        }

        const currentCoinBet = Number(Facade.model?.currentCoinBet ?? 0)
        if (Number.isFinite(currentCoinBet) && currentCoinBet > 0) {
            return currentCoinBet
        }

        return 1
    }

    /** Spin coin-value for decoding raw server amounts into credits (not for display multiply). */
    private resolveSpinCoinValueForNormalize(): number {
        const currentCoinBet = Number(Facade.model?.currentCoinBet ?? 0)
        if (Number.isFinite(currentCoinBet) && currentCoinBet > 0) {
            return currentCoinBet
        }

        const defaultCoinValue = Number((Facade.model as any)?.defaultCoinValue ?? 0)
        if (Number.isFinite(defaultCoinValue) && defaultCoinValue > 0) {
            return defaultCoinValue
        }

        return 1
    }

    private startCellRolling(cellIndex: number): void {
        const cell = this._cells[cellIndex]
        if (!cell) {
            return
        }

        if (this._lockedCoins.has(cellIndex)) {
            this.stopCellRolling(cellIndex)
            return
        }

        if (!this._hasEmittedBoardSpinStarted) {
            this._hasEmittedBoardSpinStarted = true
            ;(GameSignals as any).holdAndWinBoardSpinStarted?.emit(this._spinStartRespinsCounterValue)
        }

        this.stopCellRolling(cellIndex)
        cell.frame.visible = false
        cell.spinMask.visible = true
        cell.spinMask.alpha = 0
        this._rollingActive[cellIndex] = true

        const centerY = Facade.symbolsConfig.HEIGHT / 2
        const reelStep = this.getVerticalTravelStep()

        const runStep = () => {
            if (!this._rollingActive[cellIndex]) {
                return
            }

            const spinCoin = pickHoldAndWinSpinCoin()
            const value = this.toStakeCoinValue(spinCoin.multiplier)
            this.renderCoin(cell.symbol, value, spinCoin.coinType, false)
            cell.symbol.y = centerY - reelStep

            const duration = this._cellRollDuration[cellIndex] || this.getTimingsProfile().rollDurationMin

            this._rollingTweens[cellIndex] = gsap.to(cell.symbol, {
                y: centerY + reelStep,
                duration,
                ease: 'none',
                onComplete: () => {
                    if (!this._rollingActive[cellIndex]) {
                        return
                    }

                    const restartDelay = this._cellRestartDelay[cellIndex] || this.getTimingsProfile().restartDelayMin

                    this._rollingTweens[cellIndex] = gsap.delayedCall(restartDelay, runStep)
                },
            })
        }

        runStep()
    }

    private scheduleCellRollingStart(cellIndex: number, forceImmediate: boolean = false): void {
        const pendingCall = this._startDelayCalls[cellIndex]
        if (pendingCall) {
            pendingCall.kill()
            this._startDelayCalls[cellIndex] = null
        }

        if (forceImmediate) {
            this.startCellRolling(cellIndex)
            return
        }

        const timings = this.getTimingsProfile()
        const startDelay = Math.random() * timings.startDelayMax
        this._startDelayCalls[cellIndex] = gsap.delayedCall(startDelay, () => {
            this._startDelayCalls[cellIndex] = null
            this.startCellRolling(cellIndex)
        })
    }

    private getTimingsProfile() {
        const isFastPlay = Facade.isTurboSpin || Facade.isQuickSpin
        return isFastPlay ? BonusRespinBoardContainer.FAST_TIMINGS : BonusRespinBoardContainer.NORMAL_TIMINGS
    }

    private setupCellCadenceProfile(): void {
        const timings = this.getTimingsProfile()

        for (let index = 0; index < this._cells.length; index++) {
            this._cellRollDuration[index] =
                timings.rollDurationMin + Math.random() * (timings.rollDurationMax - timings.rollDurationMin)
            this._cellRestartDelay[index] =
                timings.restartDelayMin + Math.random() * (timings.restartDelayMax - timings.restartDelayMin)
        }
    }

    private stopCellRolling(cellIndex: number): void {
        this._rollingActive[cellIndex] = false

        const pendingCall = this._startDelayCalls[cellIndex]
        if (pendingCall) {
            pendingCall.kill()
            this._startDelayCalls[cellIndex] = null
        }

        const tween = this._rollingTweens[cellIndex]
        if (tween) {
            tween.kill()
            this._rollingTweens[cellIndex] = null
        }

        const stickyTween = this._stickyLandingTweens[cellIndex]
        if (stickyTween) {
            stickyTween.kill()
            this._stickyLandingTweens[cellIndex] = null
        }

        // If a stop/collect interrupted mid-tween, snap locked coins into the cell centre.
        const coinData = this._lockedCoins.get(cellIndex)
        const cell = this._cells[cellIndex]
        if (cell && coinData && Number(coinData.value) > 0) {
            cell.symbol.y = BonusRespinBoardContainer.CELL_SYMBOL_STOP_Y
            cell.symbol.visible = true
        }
    }

    private stopAllRollingTimers(): void {
        for (let index = 0; index < this._cells.length; index++) {
            this.stopCellRolling(index)
        }
    }

    /** Kill in-flight stop/land tweens and place every locked coin at rest (or hide empties). */
    private forceSnapAllCellsToFinalState(): void {
        for (let cellIndex = 0; cellIndex < this._cells.length; cellIndex++) {
            this.stopCellRolling(cellIndex)
            const coinData = this._lockedCoins.get(cellIndex)
            this.applyFinalCellState(cellIndex, coinData, false)
        }
    }

    private finalizeCellStop(
        cellIndex: number,
        coinData?: { value: number; type: string },
        playLanding: boolean = false,
        immediate: boolean = false
    ): void {
        const cell = this._cells[cellIndex]
        if (!cell) {
            return
        }

        this._rollingActive[cellIndex] = false

        const pendingCall = this._startDelayCalls[cellIndex]
        if (pendingCall) {
            pendingCall.kill()
            this._startDelayCalls[cellIndex] = null
        }

        const activeTween = this._rollingTweens[cellIndex]
        if (activeTween) {
            activeTween.kill()
            this._rollingTweens[cellIndex] = null
        }

        // Never let a sticky land-in fight a new exit tween (spam stop / re-finalize).
        const stickyTween = this._stickyLandingTweens[cellIndex]
        if (stickyTween) {
            stickyTween.kill()
            this._stickyLandingTweens[cellIndex] = null
        }

        const applyFinal = () => {
            this.applyFinalCellState(cellIndex, coinData, playLanding)
        }

        if (!cell.symbol.visible) {
            applyFinal()
            return
        }

        const centerY = Facade.symbolsConfig.HEIGHT / 2
        const reelStep = this.getVerticalTravelStep()
        const startY = centerY - reelStep
        const targetY = centerY + reelStep
        const currentY = Number(cell.symbol.y)
        const remainingDistance = Math.max(0, targetY - currentY)
        const fullDistance = Math.max(1, targetY - startY)
        const timings = this.getTimingsProfile()

        // Forced stop keeps a short exit-out (original feel); natural stop uses remaining distance.
        const exitDuration = immediate
            ? BonusRespinBoardContainer.IMMEDIATE_EXIT_DURATION
            : Math.max(timings.exitMinDuration, timings.rollDurationMin * (remainingDistance / fullDistance))

        this._rollingTweens[cellIndex] = gsap.to(cell.symbol, {
            y: targetY,
            duration: exitDuration,
            ease: 'none',
            onComplete: () => {
                this._rollingTweens[cellIndex] = null
                applyFinal()
            },
        })
    }

    private applyFinalCellState(
        cellIndex: number,
        coinData?: { value: number; type: string },
        playLanding: boolean = false
    ): void {
        const cell = this._cells[cellIndex]
        if (!cell) {
            return
        }

        const stickyTween = this._stickyLandingTweens[cellIndex]
        if (stickyTween) {
            stickyTween.kill()
            this._stickyLandingTweens[cellIndex] = null
        }

        this.setCellMaskEnabled(cell, true)

        if (coinData && Number(coinData.value) > 0) {
            cell.frame.visible = false
            cell.spinMask.visible = true
            cell.spinMask.alpha = 0
            cell.symbol.y = BonusRespinBoardContainer.CELL_SYMBOL_STOP_Y

            if (playLanding) {
                this.playStickyLandingFromSpin(cellIndex, Number(coinData.value), String(coinData.type || 'prize'))
                return
            }

            this.renderCoin(cell.symbol, Number(coinData.value), String(coinData.type || 'prize'), false)
            return
        }

        cell.frame.visible = false
        this.hideCellSymbol(cell)
    }

    private hideCellSymbol(cell: IBonusCell): void {
        this.setCellMaskEnabled(cell, true)
        cell.spinMask.visible = true
        cell.spinMask.alpha = 0
        cell.symbol.tint = 0xffffff
        cell.symbol.stopAnimation()
        cell.symbol.visible = false
    }

    private playStickyLandingFromSpin(cellIndex: number, value: number, coinType: string): void {
        const cell = this._cells[cellIndex]
        if (!cell) {
            return
        }

        this.setCellMaskEnabled(cell, true)

        const existingTween = this._stickyLandingTweens[cellIndex]
        if (existingTween) {
            existingTween.kill()
            this._stickyLandingTweens[cellIndex] = null
        }

        const reelStep = this.getVerticalTravelStep()
        const stopY = BonusRespinBoardContainer.CELL_SYMBOL_STOP_Y

        this.renderCoin(cell.symbol, value, coinType, false)
        cell.symbol.y = stopY - reelStep

        this._stickyLandingTweens[cellIndex] = gsap.to(cell.symbol, {
            y: stopY,
            duration: Facade.isTurboSpin || Facade.isQuickSpin ? 0.11 : 0.17,
            ease: 'none',
            onComplete: () => {
                this._stickyLandingTweens[cellIndex] = null
                this.renderCoin(cell.symbol, value, coinType, true)
                this.playStickyLandBoardShake()

                const jackpotTier = this.resolveJackpotTier(coinType)
                if (jackpotTier) {
                    // Jackpot land SFX (H&W only). Prize coins use holdAndWinCoinLanded instead.
                    ;(GameSignals as any).holdAndWinJackpotCoinLanded?.emit(jackpotTier)

                    if (!this._winAnnouncedJackpotTiers.has(jackpotTier)) {
                        this._winAnnouncedJackpotTiers.add(jackpotTier)
                        ;(GameSignals as any).holdAndWinJackpotSymbolLanded?.emit(jackpotTier)
                    }
                } else {
                    ;(GameSignals as any).holdAndWinCoinLanded?.emit(cellIndex)
                }
                if (this._waitingStickyToApplyCounter) {
                    this._waitingStickyToApplyCounter = false
                    this.applyPendingRespinsCounterUpdate()
                    ;(GameSignals as any).holdAndWinStickyStopped?.emit(cellIndex)
                }
            },
        })
    }

    private playStickyLandBoardShake(): void {
        if (this._stickyLandShakeTimeline) {
            const activeShakeTimeline = this._stickyLandShakeTimeline
            activeShakeTimeline.progress(1)
            activeShakeTimeline.kill()
            this._stickyLandShakeTimeline = null
        }

        const desktopStage =
            (Facade.app?.stage as unknown as PIXI.Container) || (Facade.stage as unknown as PIXI.Container)
        const isMobile = DeviceUtils.isMobileDevice()
        const reelsRoot = isMobile
            ? (this.parent?.children.find((child) => {
                  return this.findContainerByName(child as PIXI.Container, ContainersNames.REELS_CONTAINER) !== null
              }) as PIXI.Container | undefined)
            : undefined
        const gameRoot = Facade.container as unknown as PIXI.Container | null
        const stageRoot = Facade.app?.stage as unknown as PIXI.Container | undefined
        const reelFrameContainer = isMobile
            ? this.findContainerByName(gameRoot, ContainersNames.REEL_CONTAINER) ||
              this.findContainerByName(stageRoot || null, ContainersNames.REEL_CONTAINER)
            : null
        const targets = isMobile
            ? ([reelsRoot, this, reelFrameContainer].filter(Boolean) as PIXI.Container[])
            : [desktopStage || this]
        const screenIntensity = Facade.isTurboSpin || Facade.isQuickSpin ? 4 : 6
        const baseTransforms = targets.map((target) => ({
            target,
            x: target.x,
            y: target.y,
            scaleX: target.scale.x,
            scaleY: target.scale.y,
            worldScale: Math.max(0.01, Math.hypot(target.worldTransform.a, target.worldTransform.b)),
        }))
        const zoom = isMobile ? 1 : Facade.isTurboSpin || Facade.isQuickSpin ? 1.003 : 1.006

        const shakeState = {
            offsetX: 0,
            offsetY: 0,
            zoom: 1,
        }

        const applyState = () => {
            baseTransforms.forEach(({ target, x, y, scaleX, scaleY, worldScale }) => {
                target.position.set(x + shakeState.offsetX / worldScale, y + shakeState.offsetY / worldScale)
                if (!isMobile) {
                    target.scale.set(scaleX * shakeState.zoom, scaleY * shakeState.zoom)
                }
            })
        }

        this._stickyLandShakeTimeline = gsap.timeline({
            onComplete: () => {
                shakeState.offsetX = 0
                shakeState.offsetY = 0
                shakeState.zoom = 1
                applyState()
                this._stickyLandShakeTimeline = null
            },
        })

        this._stickyLandShakeTimeline
            .to(shakeState, {
                offsetX: -screenIntensity,
                offsetY: Math.max(1, screenIntensity * 0.35),
                zoom,
                duration: 0.035,
                ease: 'power2.out',
                onUpdate: applyState,
            })
            .to(shakeState, {
                offsetX: screenIntensity,
                offsetY: -Math.max(1, screenIntensity * 0.35),
                zoom: 1,
                duration: 0.05,
                ease: 'power1.inOut',
                onUpdate: applyState,
            })
            .to(shakeState, {
                offsetX: -Math.floor(screenIntensity * 0.45),
                offsetY: 1,
                zoom,
                duration: 0.04,
                ease: 'power1.inOut',
                onUpdate: applyState,
            })
            .to(shakeState, {
                offsetX: 0,
                offsetY: 0,
                zoom: 1,
                duration: 0.06,
                ease: 'power2.out',
                onUpdate: applyState,
            })
    }

    private hydrateLockedCoinsFromModel(): void {
        const spinData: any = Facade.model?.spinData || {}
        const extra = spinData?.hold_and_win_extra || {}
        const coinIndex: number[] = Array.isArray(extra?.coin_index) ? extra.coin_index : []
        const coinValues: number[] = Array.isArray(extra?.coin_values) ? extra.coin_values : []
        const coinTypes: string[] = Array.isArray(extra?.coin_types) ? extra.coin_types : []
        const spinCoinValue = this.resolveSpinCoinValueForNormalize()

        const locked = new Map<number, { value: number; type: string }>()

        coinIndex.forEach((rawIndex: number, idx: number) => {
            const index = Number(rawIndex)
            if (!Number.isFinite(index) || index < 0 || index >= this._cells.length) {
                return
            }

            const normalizedCredit = Number(
                normalizeHoldAndWinCoinValue(coinValues[idx], coinTypes[idx], spinCoinValue) ?? 0
            )

            locked.set(index, {
                value: this.toStakeCoinValue(normalizedCredit),
                type: String(coinTypes[idx] ?? 'prize'),
            })
        })

        this._lockedCoins = locked
    }

    private renderLockedCoins(): void {
        for (let index = 0; index < this._cells.length; index++) {
            const lockedCoin = this._lockedCoins.get(index)

            if (lockedCoin && Number(lockedCoin.value) > 0) {
                this.applyFinalCellState(index, lockedCoin)
                continue
            }

            this.applyFinalCellState(index, undefined)
        }
    }

    private renderCoin(symbol: SymbolContainer, value: number, coinType: string, land: boolean): void {
        symbol.tint = 0xffffff
        symbol.alpha = 1
        const symbolData: SymbolContainerData = {
            index: SpecialSymbolType.MULTIPLIER,
            symbolKey: 'k',
            coinType,
            coinValue: value,
            multiplier: value,
        }

        symbol.visible = true
        symbol.init({ symbolData, allowUnmasked: false })

        if (land) {
            symbol.playAnimation(['land', 'idle'], [false, true], null, true)
            return
        }

        symbol.playAnimation('idle', true, null, true)
    }

    private drawCellFrame(frame: PIXI.Graphics, active: boolean): void {
        const cellWidth = ReelsContainerHelper.calculateReelWidth() - 1
        const cellHeight = ReelsContainerHelper.calculateReelHeight() - 1
        frame.clear()
        frame.lineStyle(2, active ? 0xf3c352 : 0x2f4058, active ? 0.95 : 0.55)
        frame.beginFill(active ? 0x130f08 : 0x0c1624, active ? 0.32 : 0.18)
        frame.drawRoundedRect(0, 0, cellWidth, cellHeight, 16)
        frame.endFill()
    }

    private setCellMaskEnabled(cell: IBonusCell, enabled: boolean): void {
        if (enabled) {
            cell.symbol.mask = cell.spinMask as any
            cell.spinMask.visible = true
            return
        }

        cell.symbol.mask = null
        cell.spinMask.visible = false
    }

    private resetAllCellMasks(enabled: boolean): void {
        this._cells.forEach((cell) => {
            this.setCellMaskEnabled(cell, enabled)
        })
    }

    private getVerticalTravelStep(): number {
        // Use mask height as source of truth and overscan a lot so the reset is never visible.
        const cellHeight = ReelsContainerHelper.calculateReelHeight()
        return Math.max(220, Math.floor(cellHeight * 1.85))
    }

    private getRandomStopOrder(): number[] {
        const order = Array.from({ length: this._cells.length }, (_, index) => index)

        for (let index = order.length - 1; index > 0; index--) {
            const randomIndex = Math.floor(Math.random() * (index + 1))
            const tmp = order[index]
            order[index] = order[randomIndex]
            order[randomIndex] = tmp
        }

        return order
    }

    private buildStopOrder(candidates: number[], priority: number[]): number[] {
        const prioritySet = new Set(priority)
        const priorityPart = this.shuffle(priority.filter((index) => candidates.includes(index)))
        const restPart = this.shuffle(candidates.filter((index) => !prioritySet.has(index)))

        return [...priorityPart, ...restPart]
    }

    private shuffle(values: number[]): number[] {
        const arr = values.slice()

        for (let index = arr.length - 1; index > 0; index--) {
            const randomIndex = Math.floor(Math.random() * (index + 1))
            const tmp = arr[index]
            arr[index] = arr[randomIndex]
            arr[randomIndex] = tmp
        }

        return arr
    }

    private sortEntriesLeftToRight(
        entries: Array<[number, { value: number; type: string }]>
    ): Array<[number, { value: number; type: string }]> {
        const columns = BonusRespinBoardContainer.COLUMNS

        return entries.slice().sort(([aIndex], [bIndex]) => {
            const aCol = aIndex % columns
            const bCol = bIndex % columns

            if (aCol !== bCol) {
                return aCol - bCol
            }

            const aRow = Math.floor(aIndex / columns)
            const bRow = Math.floor(bIndex / columns)

            return aRow - bRow
        })
    }

    private isJackpotCoinType(coinType: string): boolean {
        return this.resolveJackpotTier(coinType) !== null
    }

    private announcePresentJackpotTiers(): void {
        const spinType = String((Facade.model?.spinData as any)?.spin_type || '').toLowerCase()
        // A non-respin spin is the feature-trigger spin (the Continue moment): start a fresh
        // announcement set so jackpots already on the board play their box win animation.
        if (spinType !== 'respin') {
            this._winAnnouncedJackpotTiers.clear()
        }

        this._lockedCoins.forEach((coinData) => {
            const tier = this.resolveJackpotTier(String(coinData?.type || ''))
            if (tier && !this._winAnnouncedJackpotTiers.has(tier)) {
                this._winAnnouncedJackpotTiers.add(tier)
                ;(GameSignals as any).holdAndWinJackpotSymbolLanded?.emit(tier)
            }
        })
    }

    public getCollectedJackpotTiers(): HoldAndWinJackpotTier[] {
        const present = new Set<HoldAndWinJackpotTier>()

        this._lockedCoins.forEach((coinData) => {
            const tier = this.resolveJackpotTier(String(coinData?.type || ''))
            if (tier) {
                present.add(tier)
            }
        })

        return HOLD_AND_WIN_JACKPOT_TIER_PRIORITY.filter((tier) => present.has(tier))
    }

    public getPendingJackpotPopupTiers(): HoldAndWinJackpotTier[] {
        // Capped round: the collect presentation was dropped, so its leftover popups are dropped
        // with it — only the commendation, the total win popup and the max exposure message run.
        if (this._hasSkippedCollectForCap) {
            return []
        }

        if (this.isFullGoldenCoinBoard()) {
            return this._hasShownFullBoardMiniPopupDuringCollect ? [] : ['mini']
        }

        // One popup per jackpot tier — pending only if that tier was never shown inline.
        return this.getCollectedJackpotTiers().filter((tier) => !this._jackpotPopupsShownDuringCollect.has(tier))
    }

    public isFullGoldenCoinBoard(): boolean {
        if (!this.isFullBoard()) {
            return false
        }

        // ...and none of them is a jackpot symbol (so all are Golden Coins).
        return this.getCollectedJackpotTiers().length === 0
    }

    private isFullBoard(): boolean {
        return this._lockedCoins.size === BonusRespinBoardContainer.CELLS_COUNT
    }

    private resolveJackpotTier(coinType: string): 'grand' | 'major' | 'minor' | 'mini' | null {
        const normalized = String(coinType || '').toLowerCase()

        if (normalized.includes('grand')) {
            return 'grand'
        }
        if (normalized.includes('major')) {
            return 'major'
        }
        // 'minor' must be checked before 'mini' because 'minor' also contains 'min'.
        if (normalized.includes('minor')) {
            return 'minor'
        }
        if (normalized.includes('mini')) {
            return 'mini'
        }

        return null
    }
}
